import { useCallback, useEffect, useState } from 'react';
import { ICON_PRICE } from '@/data/titleColorPalette';
import { useLocale } from '@/context/LocaleContext';
import { assetUrl } from '@/lib/assetUrl';
import { useSilkBalance } from '@/hooks/useSilkBalance';
import {
  useIcons,
  useTitleManagerMutations,
  useUserIcons,
  type IconRow,
} from '@/hooks/useTitleManager';
import { BuyIconModal } from './BuyIconModal';
import { notifyFromResult, type TmNotifyType } from './titleManagerUtils';

interface PendingIconBuy {
  iconId: number;
  iconName: string;
  iconUrl: string;
  position: 'left' | 'right';
}

interface IconTabProps {
  onNotify: (msg: string, type?: TmNotifyType) => void;
}

function IconGrid({
  icons,
  position,
  activeId,
  onIconClick,
}: {
  icons: IconRow[];
  position: 'left' | 'right';
  activeId: number | null;
  onIconClick: (icon: IconRow, owned: boolean) => void;
}) {
  const { t } = useLocale();

  if (icons.length === 0) {
    return <div className="iconmanager-no-icons">{t('tm_no_icons')}</div>;
  }

  return (
    <>
      {icons.map((icon) => {
        const owned = position === 'left' ? Boolean(icon.owned_left) : Boolean(icon.owned_right);
        const isActive = activeId != null && Number(icon.ID) === Number(activeId);
        const iconClass = owned ? 'iconmanager-icon owned' : 'iconmanager-icon not-owned';
        const title = owned ? icon.CodeName : `${icon.CodeName} - ${ICON_PRICE} Silks`;

        return (
          <div
            key={`${position}-${icon.ID}`}
            className={`${iconClass}${isActive ? ' active' : ''}`}
            title={title}
            role="button"
            tabIndex={0}
            onClick={() => onIconClick(icon, owned)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onIconClick(icon, owned);
            }}
          >
            <img
              className="iconmanager-icon-preview-img"
              src={assetUrl(icon.icon_url)}
              alt={icon.CodeName}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = assetUrl('/assets/images/icon/placeholder.webp');
              }}
            />
            {isActive ? (
              <span className="icon-active-check">
                <i className="fas fa-check" aria-hidden />
              </span>
            ) : null}
            {!owned ? (
              <span className="icon-price">
                <i className="fas fa-gem" aria-hidden />
              </span>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

export function IconTab({ onNotify }: IconTabProps) {
  const { t } = useLocale();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(id);
  }, [search]);

  const { data: icons = [], isLoading } = useIcons(debouncedSearch);
  const { data: userIcons } = useUserIcons();
  const { buyIcon, applyIcon, reloadIcons } = useTitleManagerMutations();

  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [pendingBuy, setPendingBuy] = useState<PendingIconBuy | null>(null);
  const { data: balance, isLoading: balanceLoading, refetch: refetchBalance } = useSilkBalance(
    buyModalOpen,
  );

  const activeLeftId = userIcons?.activeLeft?.ID ?? null;
  const activeRightId = userIcons?.activeRight?.ID ?? null;

  const handleIconClick = useCallback(
    async (icon: IconRow, owned: boolean, position: 'left' | 'right') => {
      if (owned) {
        try {
          const result = await applyIcon.mutateAsync({ icon_id: icon.ID, position });
          if (result.success) await reloadIcons();
          notifyFromResult(result, t, onNotify, 'tm_error_applying_icon');
        } catch {
          onNotify(t('tm_error_applying_icon'), 'danger');
        }
        return;
      }

      try {
        await refetchBalance();
        setPendingBuy({
          iconId: icon.ID,
          iconName: icon.CodeName,
          iconUrl: icon.icon_url,
          position,
        });
        setBuyModalOpen(true);
      } catch {
        onNotify(t('tm_error_getting_balance'), 'danger');
      }
    },
    [applyIcon, onNotify, refetchBalance, reloadIcons, t],
  );

  const handleBuyConfirm = useCallback(async () => {
    if (!pendingBuy) return;
    const { iconId, position } = pendingBuy;
    try {
      const result = await buyIcon.mutateAsync({ icon_id: iconId, position });
      if (result.success) {
        setBuyModalOpen(false);
        setPendingBuy(null);
        await reloadIcons();
        notifyFromResult(result, t, onNotify, 'tm_error_buying_icon');
      } else {
        notifyFromResult(result, t, onNotify, 'tm_error_buying_icon');
      }
    } catch {
      onNotify(t('tm_error_buying_icon'), 'danger');
    }
  }, [applyIcon, buyIcon, onNotify, pendingBuy, reloadIcons, t]);

  return (
    <>
      <div className="iconmanager-content">
        <div className="iconmanager-search">
          <input
            type="text"
            className="titlemanager-input"
            placeholder={t('tm_icon_search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="iconmanager-price-label">
            <i className="fas fa-gem" aria-hidden /> {t('tm_price_icon_label', { amount: ICON_PRICE })}
          </span>
        </div>

        <div className="iconmanager-grid">
          <div className="iconmanager-list">
            <div className="iconmanager-header">
              <i className="fas fa-arrow-left" aria-hidden /> {t('tm_icon_left')}
            </div>
            <div className="iconmanager-separator">
              <div className="iconmanager-separator-line" />
              <i className="fas fa-star iconmanager-separator-icon" aria-hidden />
              <div className="iconmanager-separator-line" />
            </div>
            <div className="iconmanager-icons-grid">
              {isLoading ? (
                <div className="iconmanager-no-icons">{t('loading_data')}</div>
              ) : (
                <IconGrid
                  icons={icons}
                  position="left"
                  activeId={activeLeftId}
                  onIconClick={(icon, owned) => void handleIconClick(icon, owned, 'left')}
                />
              )}
            </div>
          </div>

          <div className="iconmanager-editor">
            <div className="iconmanager-header">
              {t('tm_icon_right')} <i className="fas fa-arrow-right" aria-hidden />
            </div>
            <div className="iconmanager-separator">
              <div className="iconmanager-separator-line" />
              <i className="fas fa-star iconmanager-separator-icon" aria-hidden />
              <div className="iconmanager-separator-line" />
            </div>
            <div className="iconmanager-icons-grid">
              {isLoading ? (
                <div className="iconmanager-no-icons">{t('loading_data')}</div>
              ) : (
                <IconGrid
                  icons={icons}
                  position="right"
                  activeId={activeRightId}
                  onIconClick={(icon, owned) => void handleIconClick(icon, owned, 'right')}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <BuyIconModal
        open={buyModalOpen}
        iconName={pendingBuy?.iconName ?? ''}
        iconUrl={pendingBuy?.iconUrl ?? ''}
        price={ICON_PRICE}
        balance={balance ?? null}
        balanceLoading={balanceLoading}
        confirming={buyIcon.isPending || applyIcon.isPending}
        onConfirm={() => void handleBuyConfirm()}
        onClose={() => {
          if (!buyIcon.isPending && !applyIcon.isPending) {
            setBuyModalOpen(false);
            setPendingBuy(null);
          }
        }}
      />
    </>
  );
}
